import { useCallback, useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { Link, useSearchParams } from 'react-router-dom'
import { ApiClientError } from '../api/client'
import {
  createProject,
  deleteProject,
  getAuthenticatedIconUrl,
  getProjects,
  uploadProjectIcon,
} from '../api/projects'
import { listEmbeddingProviders, listLlmProviders } from '../api/providers'
import { getSettings } from '../api/settings'
import Modal from '../components/Modal'
import { useAuth } from '../hooks/useAuth'
import type { EmbeddingProviderEntry, LlmProviderEntry, ProjectResponse } from '../types/api'
import { formatDate } from '../utils/format'
import { validateProjectName } from '../utils/project-name'
import { canManageProject, isGlobalAdmin, projectRoleLabel } from '../utils/rbac'

function ProjectIcon({ iconUrl, name }: { iconUrl?: string | null; name: string }) {
  const [imgError, setImgError] = useState(false)
  const authenticatedUrl = getAuthenticatedIconUrl(iconUrl)
  if (authenticatedUrl && !imgError) {
    return (
      <img
        src={authenticatedUrl}
        alt=""
        className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
        onError={() => setImgError(true)}
      />
    )
  }
  return (
    <div className="w-10 h-10 rounded-lg bg-emerald-600/20 text-emerald-400 flex items-center justify-center text-lg font-bold flex-shrink-0">
      {name[0]?.toUpperCase() ?? '?'}
    </div>
  )
}

export default function ProjectsPage() {
  const { user } = useAuth()
  const isAdmin = isGlobalAdmin(user?.role)
  const [projects, setProjects] = useState<ProjectResponse[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [newProjectName, setNewProjectName] = useState('')
  const [newProjectDescription, setNewProjectDescription] = useState('')
  const [isCreating, setIsCreating] = useState(false)
  const [systemEmbeddingProviders, setSystemEmbeddingProviders] = useState<
    EmbeddingProviderEntry[]
  >([])
  const [selectedEmbeddingProviderId, setSelectedEmbeddingProviderId] = useState<number | ''>('')
  const [systemLlmProviders, setSystemLlmProviders] = useState<LlmProviderEntry[]>([])
  const [selectedLlmProviderId, setSelectedLlmProviderId] = useState<number | ''>('')
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean
    projectId: number | null
    projectName: string
  }>({ isOpen: false, projectId: null, projectName: '' })
  const [isDeleting, setIsDeleting] = useState(false)
  const [newProjectIcon, setNewProjectIcon] = useState<File | null>(null)
  const [projectNameError, setProjectNameError] = useState<string | null>(null)
  const iconFileInputRef = useRef<HTMLInputElement>(null)
  const [searchParams, setSearchParams] = useSearchParams()

  // Deep link: /projects?create=1 auto-opens the create dialog (used by the
  // Project Setup empty state). The param is consumed so back/refresh
  // doesn't reopen the modal.
  useEffect(() => {
    if (!isAdmin || searchParams.get('create') !== '1') return
    setShowCreateModal(true)
    searchParams.delete('create')
    setSearchParams(searchParams, { replace: true })
  }, [isAdmin, searchParams, setSearchParams])

  const fetchProjects = useCallback(async () => {
    try {
      const data = await getProjects()
      setProjects(data)
    } catch {
      toast.error('Failed to load projects')
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchProjects()
  }, [fetchProjects])

  useEffect(() => {
    if (!isAdmin) return
    void Promise.allSettled([listEmbeddingProviders(), getSettings()]).then(
      ([providersOutcome, settingsOutcome]) => {
        if (providersOutcome.status !== 'fulfilled') {
          toast.error('Failed to load embedding providers')
          return
        }
        // R8: hide the `local` option for new projects when the platform
        // disables it (already-saved rows are unaffected — this is create-only).
        const localOk =
          settingsOutcome.status === 'fulfilled'
            ? (settingsOutcome.value.localEmbeddingSupported ?? true)
            : true
        const active = providersOutcome.value.filter(
          (r) => r.isActive && (localOk || r.provider !== 'local')
        )
        setSystemEmbeddingProviders(active)
        const preferred = active.find((r) => r.isDefault) ?? active[0]
        setSelectedEmbeddingProviderId((current) =>
          current === '' && preferred ? preferred.id : current
        )
      }
    )
    listLlmProviders()
      .then((rows) => setSystemLlmProviders(rows.filter((r) => r.isActive)))
      .catch(() => {})
  }, [isAdmin])

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (isCreating) return

    const nameError = validateProjectName(newProjectName)
    setProjectNameError(nameError)
    if (nameError) return

    if (selectedEmbeddingProviderId === '') {
      toast.error('Select an embedding provider')
      return
    }
    setIsCreating(true)

    try {
      const created = await createProject(
        newProjectName.trim(),
        newProjectDescription || undefined,
        { embeddingProviderId: selectedEmbeddingProviderId },
        selectedLlmProviderId === '' ? undefined : selectedLlmProviderId
      )
      if (newProjectIcon) {
        try {
          await uploadProjectIcon(created.id, newProjectIcon)
        } catch {
          toast.success('Project created (icon upload failed)')
        }
      }
      toast.success('Project created successfully')
      setShowCreateModal(false)
      setProjectNameError(null)
      setNewProjectName('')
      setNewProjectDescription('')
      setNewProjectIcon(null)
      await fetchProjects()
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to create project'
      if (/project name|capital letters|spaces are not allowed|special characters/i.test(message)) {
        setProjectNameError(message)
      } else {
        toast.error(message)
      }
    } finally {
      setIsCreating(false)
    }
  }

  const handleDelete = async () => {
    if (!deleteModal.projectId || isDeleting) return
    setIsDeleting(true)

    try {
      await deleteProject(deleteModal.projectId)
      toast.success('Project deleted')
      setDeleteModal({ isOpen: false, projectId: null, projectName: '' })
      await fetchProjects()
    } catch (error) {
      const message =
        error instanceof ApiClientError
          ? error.message
          : error instanceof Error
            ? error.message
            : 'Failed to delete project'
      toast.error(message)
    } finally {
      setIsDeleting(false)
    }
  }

  const openDeleteModal = (id: number, name: string) => {
    setDeleteModal({ isOpen: true, projectId: id, projectName: name })
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-emerald-500"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-harbor-text">Projects</h1>
          <p className="text-harbor-text-secondary mt-1">Manage your RAG projects</p>
        </div>
        {isAdmin && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-emerald-600 text-harbor-text text-sm font-medium rounded-lg hover:bg-emerald-700 transition-colors"
          >
            Create Project
          </button>
        )}
      </div>

      {/* Projects List */}
      {projects.length === 0 ? (
        <div className="bg-harbor-surface border border-harbor-border rounded-xl p-12 text-center">
          <div className="text-5xl mb-4">📁</div>
          <h3 className="text-lg font-semibold text-harbor-text mb-2">No projects yet</h3>
          <p className="text-sm text-harbor-text-secondary mb-4">
            {isAdmin
              ? 'Create your first project to start indexing documents'
              : 'Ask an administrator to create a project or grant you access'}
          </p>
          {isAdmin && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-4 py-2 bg-emerald-600 text-harbor-text text-sm font-medium rounded-lg hover:bg-emerald-700 transition-colors"
            >
              Create Project
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {projects.map((project) => {
            const canManage = canManageProject(project.projectRole)
            return (
              <div
                key={project.id}
                className="bg-harbor-surface border border-harbor-border rounded-xl p-6 hover:border-harbor-border transition-colors"
              >
                <div className="flex items-start justify-between">
                  <Link to={`/projects/${project.id}`} className="flex-1">
                    <div className="flex items-center gap-3 flex-wrap">
                      <ProjectIcon
                        key={project.iconUrl ?? 'no-icon'}
                        iconUrl={project.iconUrl}
                        name={project.name}
                      />
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="text-lg font-semibold text-harbor-text hover:text-emerald-400 transition-colors">
                            {project.name}
                          </h3>
                          {project.projectRole && (
                            <span className="text-xs px-2 py-0.5 rounded-md bg-harbor-surface-raised text-harbor-text-secondary">
                              {projectRoleLabel(project.projectRole)}
                            </span>
                          )}
                        </div>
                        {project.description && (
                          <p className="text-sm text-harbor-text-secondary mt-1 line-clamp-2">
                            {project.description}
                          </p>
                        )}
                      </div>
                    </div>
                  </Link>
                  {isAdmin && (
                    <button
                      onClick={() => openDeleteModal(project.id, project.name)}
                      className="p-2 text-harbor-text-muted hover:text-red-400 rounded-lg hover:bg-harbor-surface-raised transition-colors"
                      title="Delete project"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                    </button>
                  )}
                </div>
                <div className="mt-4 pt-4 border-t border-harbor-border">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-harbor-text-muted">
                      Created {formatDate(project.createdAt)}
                    </span>
                    {canManage && (
                      <Link
                        to={`/projects/${project.id}/upload`}
                        className="text-emerald-400 hover:text-emerald-300"
                      >
                        Upload files →
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Create Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false)
          setProjectNameError(null)
        }}
        title="Create Project"
        size="md"
      >
        <form onSubmit={handleCreate} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-harbor-text-secondary mb-1">
              Project Name
            </label>
            <input
              type="text"
              value={newProjectName}
              onChange={(e) => {
                const value = e.target.value
                setNewProjectName(value)
                // Live feedback for illegal chars/convention; keep the hint while empty.
                setProjectNameError(value.length === 0 ? null : validateProjectName(value))
              }}
              onBlur={() => {
                if (newProjectName) setProjectNameError(validateProjectName(newProjectName))
              }}
              required
              maxLength={64}
              aria-invalid={projectNameError ? 'true' : undefined}
              aria-describedby={projectNameError ? 'project-name-error' : 'project-name-hint'}
              className={`w-full px-3 py-2 bg-harbor-surface-raised border rounded-lg text-harbor-text placeholder-harbor-text-muted focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent ${
                projectNameError ? 'border-red-500' : 'border-harbor-border'
              }`}
              placeholder="my-project"
            />
            {projectNameError ? (
              <p id="project-name-error" className="mt-1 text-xs text-red-400">
                {projectNameError}
              </p>
            ) : (
              <p id="project-name-hint" className="mt-1 text-xs text-harbor-text-muted">
                Lowercase only, start with a letter, 1–64 chars. Allowed: a–z, 0–9, - and _. e.g.
                my-project, my_project, project1.
              </p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-harbor-text-secondary mb-1">
              Description (optional)
            </label>
            <textarea
              value={newProjectDescription}
              onChange={(e) => setNewProjectDescription(e.target.value)}
              maxLength={1024}
              rows={3}
              className="w-full px-3 py-2 bg-harbor-surface-raised border border-harbor-border rounded-lg text-harbor-text placeholder-harbor-text-muted focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent resize-none"
              placeholder="Brief description of your project"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-harbor-text-secondary mb-1">
              Icon (optional)
            </label>
            <div className="flex items-center gap-4">
              {newProjectIcon ? (
                <img
                  src={URL.createObjectURL(newProjectIcon)}
                  alt=""
                  className="w-16 h-16 rounded-lg object-cover"
                />
              ) : (
                <div className="w-16 h-16 rounded-lg bg-harbor-surface-raised border border-harbor-border flex items-center justify-center text-harbor-text-muted">
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                </div>
              )}
              <div className="flex flex-col gap-2">
                <input
                  ref={iconFileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
                  className="hidden"
                  onChange={(e) => setNewProjectIcon(e.target.files?.[0] ?? null)}
                />
                <button
                  type="button"
                  onClick={() => iconFileInputRef.current?.click()}
                  className="px-3 py-1.5 text-sm bg-harbor-surface-raised border border-harbor-border rounded-lg hover:bg-harbor-surface transition-colors"
                >
                  {newProjectIcon ? 'Change icon' : 'Choose icon'}
                </button>
                {newProjectIcon && (
                  <button
                    type="button"
                    onClick={() => {
                      setNewProjectIcon(null)
                      if (iconFileInputRef.current) iconFileInputRef.current.value = ''
                    }}
                    className="px-3 py-1.5 text-sm text-red-400 hover:text-red-300 hover:bg-harbor-surface-raised rounded-lg transition-colors"
                  >
                    Remove
                  </button>
                )}
              </div>
            </div>
            <p className="text-xs text-harbor-text-muted mt-1">
              Recommended: 128 × 128 px. Images are resized to fit.
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-harbor-text-secondary mb-1">
              Embedding provider
            </label>
            {systemEmbeddingProviders.length > 0 ? (
              <>
                <select
                  value={selectedEmbeddingProviderId}
                  onChange={(e) => setSelectedEmbeddingProviderId(Number(e.target.value))}
                  className="w-full px-3 py-2 bg-harbor-surface-raised border border-harbor-border rounded-lg text-harbor-text focus:outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  {systemEmbeddingProviders.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.label} — {p.provider} ({p.modelName ?? 'no model'})
                      {p.isDefault ? ' · default' : ''}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-harbor-text-muted">
                  Provider and model are locked once the project is created. Manage the list in
                  Settings → Embedding Provider.
                </p>
              </>
            ) : (
              <p className="text-sm text-amber-400">
                No embedding providers configured yet. Add one in Settings → Embedding Provider
                first.
              </p>
            )}
          </div>
          {systemLlmProviders.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-harbor-text-secondary mb-1">
                LLM provider (optional override)
              </label>
              <select
                value={selectedLlmProviderId}
                onChange={(e) =>
                  setSelectedLlmProviderId(e.target.value === '' ? '' : Number(e.target.value))
                }
                className="w-full px-3 py-2 bg-harbor-surface-raised border border-harbor-border rounded-lg text-harbor-text focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                <option value="">Inherit system default</option>
                {systemLlmProviders.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.label} — {p.modelName}
                    {p.isDefault ? ' (default)' : ''}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={() => {
                setShowCreateModal(false)
                setProjectNameError(null)
              }}
              className="px-4 py-2 text-sm text-harbor-text-secondary hover:text-harbor-text hover:bg-harbor-surface-raised rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isCreating || systemEmbeddingProviders.length === 0 || !!projectNameError}
              className="px-4 py-2 bg-emerald-600 text-harbor-text text-sm font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isCreating ? 'Creating...' : 'Create'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, projectId: null, projectName: '' })}
        title="Delete Project"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-harbor-text-secondary">
            Are you sure you want to delete{' '}
            <span className="font-semibold text-harbor-text">{deleteModal.projectName}</span>? This
            will permanently remove all files, vectors, and project data.
          </p>
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setDeleteModal({ isOpen: false, projectId: null, projectName: '' })}
              className="px-4 py-2 text-sm text-harbor-text-secondary hover:text-harbor-text hover:bg-harbor-surface-raised rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleDelete}
              disabled={isDeleting}
              className="px-4 py-2 bg-red-600 text-harbor-text text-sm font-medium rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isDeleting ? 'Deleting...' : 'Delete Project'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  )
}
